import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CheckCircle from '@material-ui/icons/CheckCircle';
import compose from 'recompose/compose';
import Drawer from '@material-ui/core/Drawer';
import EmptyCircle from 'shared/SvgIcons/EmptyCircleSvg';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Accordion  from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import IconButton from '@material-ui/core/IconButton';
import KioskLog from 'models/KioskLog';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import PropTypes from 'prop-types';
import React from 'react';
import toJS from 'shared/components/toJS';
import Typography from '@material-ui/core/Typography';
import { actions as productFilterActions } from 'redux/domain/ProductFilter';
import { bindActionCreators } from 'redux';
import { cancelRequest, getCancelTokenSource } from 'api/Helpers';
import { connect } from 'react-redux';
import ProductFilters, {
	DEFAULT,
	ATOZ,
	ZTOA,
	LOWTOHIGH,
	HIGHTOLOW,
	NEWEST,
} from 'models/ProductFilters';
import { withTheme, withStyles } from '@material-ui/core/styles';
import Filter from '../components/Filter';
import styles from '../styles.css';

const sortByOptions = [
	{ id: DEFAULT, primaryText: 'Default' },
	{ id: ATOZ, primaryText: 'Name: A to Z' },
	{ id: ZTOA, primaryText: 'Name: Z to A' },
	{ id: LOWTOHIGH, primaryText: 'Price: Low to High' },
	{ id: HIGHTOLOW, primaryText: 'Price: High to Low' },
	{ id: NEWEST, primaryText: 'Newest' },
];

const stylesMUI = theme => ({
	root: {
		width: '100%',
		flex: '1 0 auto',
		height: '100vh',
		display: 'flex',
		position: 'fixed',
		overflowY: 'auto',
		flexDirection: 'column',
		color: theme.palette.text.primary,
		fontFamily: theme.typography.fontFamily,
		[theme.breakpoints.up('xs')]: {
			width: '100%',
		},
		[theme.breakpoints.up('sm')]: {
			width: '50%',
			minWidth: 360,
		},
		[theme.breakpoints.up('md')]: {
			width: '33%',
		},
		[theme.breakpoints.up('lg')]: {
			width: '25%',
		},
	},
	filterCheckboxLabel: {
		fontSize: '1.2em',
	},
	filterCheckboxLabelRoot: {
		width: '100%',
	},
	filterButton: {
		backgroundColor: theme.palette.common.white,
	},
});

class FilterContainer extends React.Component {
	constructor() {
		super();

		this.state = { open: false, anchorEl: null };
		this.cancelSource = getCancelTokenSource();
		this.handleSpecAttributeChange = this.handleSpecAttributeChange.bind(this);
		this.handleValueChange = this.handleValueChange.bind(this);
		this.buildSpecAttributesTable = this.buildSpecAttributesTable.bind(this);
		this.getApplicableAttributeOptions = this.getApplicableAttributeOptions.bind(this);
		this.goResetFilters = this.goResetFilters.bind(this);
		this.getBorderStyle = this.getBorderStyle.bind(this);
	}

	componentDidMount() {
		this.getSpecAttributesAsync();
	}

	componentWillUnmount() {
		// if a request is in progress stop it.
		cancelRequest(this.cancelSource);
	}

	getApplicableAttributeOptions() {
		const filterableOptionsThisCat = this.props.filterableOptions[this.props.catIdTerm];
		const { attributes, attributeOptions } = this.props;
		if (filterableOptionsThisCat && Object.keys(attributes).length !== 0) {
			const specAttributesObj = {};
			filterableOptionsThisCat.forEach(optionId => {
				const attributeOption = attributeOptions[optionId];
				if (attributeOption !== undefined) {
					const attribute = attributes[attributeOption.attributeId];
					if (!specAttributesObj[attributeOption.attributeId]) {
						specAttributesObj[attributeOption.attributeId] = {
							id: attributeOption.attributeId,
							name: attribute.name,
							displayOrder: attribute.displayOrder,
							options: [],
						};
					}
					specAttributesObj[attributeOption.attributeId].options.push({
						id: optionId,
						name: attributeOption.name,
						displayOrder: attributeOption.displayOrder,
						colorRGB: attributeOption.colorRGB,
					});
				}
			});
			return Object.values(specAttributesObj);
		}
		return [];
	}

	/**
	 * Retreive all specification attributes available in eSTORIS.
	 * These can be used for dynamic filter options.
	 */
	getSpecAttributesAsync() {
		const { getSpecAttributes, updateFetchError } = this.props;

		try {
			getSpecAttributes();
		} catch (error) {
			const log = new KioskLog(
				error,
				'Error file location: features/ProductFilter/containers/FilterContainer',
			);
			log.postMessage();
			updateFetchError(error);
		}
	}

	/**
	 * Determines if any of the options being passed in are currently part
	 * of the active filter criteria, and if so, returns a css border object
	 * to display around the component that contains these options, showing
	 * that this filter is active.
	 * @param {Array} options list of options for the current attribute.
	 */
	getBorderStyle(options) {
		const { theme: { palette: { secondary: { main } } } } = this.props;
		const { filters: { specAttributes: currentFilters } } = this.props;
		// Check if any of the options are part of the current active filters.
		const foundOne = options.find(option => currentFilters.includes(option.id));
		return foundOne !== undefined ? { border: `solid ${main} 5px` } : {};
	}

	/**
	 * Reset all filters in the filter drawer.
	 */
	goResetFilters() {
		this.props.resetFilters();
	}

	/**
	 * Handle any option changes made by the user.
	 * @param {object} options : options to update.
	 * ex: {
	 *  maxPrice: 75,
	 * }
	 */
	handleValueChange(options) {
		// Always reset to the first page since our filter criteria has changed.
		const updatedOptions = Object.assign({}, options, { page: 0 });
		this.props.receiveFilters(updatedOptions);
	}

	/**
	 * Update the list of spec attributes based on the user's toggle action.
	 * @param {boolean} isInputChecked toggle value
	 * @param {number} id specAttribute toggled
	 */
	handleSpecAttributeChange(isInputChecked, id) {
		// These are the spec attributes used in the last query.
		const currSelectedAttributes = Object.assign([], this.props.filters.specAttributes);

		if (isInputChecked) {
			currSelectedAttributes.push(id);
		} else {
			const elementPos = currSelectedAttributes.indexOf(id);
			delete currSelectedAttributes[elementPos];
		}

		this.handleValueChange({ specAttributes: currSelectedAttributes });
	}

	/**
	 * Build a table of options to display to the user.
	 * @param {Array} attributes list of attributes (options) to build
	 * @param {function} onToggle callback for when the user changes a toggle
	 * @param {ProductFilters} filters current product filter settings
	 */
	buildSpecAttributesTable() {
		const attributes = this.getApplicableAttributeOptions();
		const onToggle = this.handleSpecAttributeChange;
		const { filters, classes } = this.props;
		return attributes.length === 0
			? null
			: attributes.map(attribute => (
					<div key={attribute.id} className={styles['filter-card']}>
						<Accordion style={this.getBorderStyle(attribute.options)}>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography variant="subtitle2">{`${attribute.name}`}</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<ul className={styles['filter-options']}>
									<FormGroup>
										{attribute.options.map(option => (
											<li key={option.id}>
												{option.colorRGB ? (
													/* color was specified so switch to icons that can display colors */
													<FormControlLabel
														control={
															<Checkbox
																onChange={(event, checked) => onToggle(checked, option.id)}
																checkedIcon={
																	<CheckCircle
																		style={{
																			fill: option.colorRGB,
																		}}
																	/>
																}
																icon={
																	<EmptyCircle
																		style={{
																			fill: option.colorRGB,
																		}}
																	/>
																}
																checked={filters.specAttributes.includes(option.id)}
															/>
														}
														label={option.name}
														classes={{
															label: classes.filterCheckboxLabel,
															root: classes.filterCheckboxLabelRoot,
														}}
													/>
												) : (
													<FormControlLabel
														control={
															<Checkbox
																onChange={(event, checked) => onToggle(checked, option.id)}
																checked={filters.specAttributes.includes(option.id)}
															/>
														}
														label={option.name}
														classes={{
															label: classes.filterCheckboxLabel,
															root: classes.filterCheckboxLabelRoot,
														}}
													/>
												)}
											</li>
										))}
									</FormGroup>
								</ul>
							</AccordionDetails>
						</Accordion>
					</div>
				));
	}

	handleClick = event => this.setState({ anchorEl: event.currentTarget });

	handleClose = () => this.setState({ anchorEl: null });

	handleToggle = () => this.setState({ open: !this.state.open });

	render() {
		const { classes, displayFilterMessage, filters, filtersOn } = this.props;
		const { anchorEl } = this.state;

		// hide the filter options unless we have products to display
		return filtersOn ? (
			<div>
				<div className={styles['filter-button']}>
					<Button
						variant="contained"
						color="inherit"
						className={classes.filterButton}
						onClick={this.handleToggle}
					>
						See All Filters
					</Button>
					<span className={styles['sort-container']}>
						<span className={styles['sort-label']}>Sort By</span>
						<IconButton
							disableRipple
							style={{
								position: 'relative',
								bottom: '8px', // when this isn't used page spacing breaks
								height: 'auto',
								backgroundColor: 'transparent',
								color: 'inherit',
							}}
							onClick={this.handleClick}
						>
							<ExpandMoreIcon />
						</IconButton>
						<Menu
							id="simple-menu"
							anchorEl={anchorEl}
							open={Boolean(anchorEl)}
							onClose={this.handleClose}
							getContentAnchorEl={null}
							anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
						>
							{sortByOptions.map(option => (
								<MenuItem
									key={option.id}
									selected={option.id === filters.orderBy}
									onClick={() => {
										this.handleValueChange({ orderBy: option.id });
										this.handleClose();
									}}
									style={option.id === filters.orderBy ? { fontWeight: 'bold' } : {}}
								>
									{option.primaryText}
								</MenuItem>
							))}
						</Menu>
					</span>
				</div>
				<Drawer
					onClose={this.handleToggle}
					open={this.state.open}
					PaperProps={{ className: classes.root }}
					keepMounted
				>
					<Filter
						buildSpecAttributesTable={this.buildSpecAttributesTable}
						displayMessage={displayFilterMessage}
						goResetFilters={this.goResetFilters}
						handleValueChange={this.handleValueChange}
						inStockChecked={filters.inStockOnly}
						onDisplayChecked={filters.onDisplay}
						toggleDrawer={this.handleToggle}
					/>
				</Drawer>
			</div>
		) : null;
	}
}

FilterContainer.propTypes = {
	attributes: PropTypes.objectOf(
		PropTypes.shape({
			name: PropTypes.string,
			displayOrder: PropTypes.number,
			optionIds: PropTypes.arrayOf(PropTypes.number),
		}),
	).isRequired,
	attributeOptions: PropTypes.objectOf(
		PropTypes.shape({
			name: PropTypes.string,
			displayOrder: PropTypes.number,
			colorRGB: PropTypes.string,
			attributeId: PropTypes.number,
		}),
	).isRequired,
	catIdTerm: PropTypes.string,
	classes: PropTypes.shape({
		root: PropTypes.string,
		filterCheckboxLabel: PropTypes.string,
		filterCheckboxLabelRoot: PropTypes.string,
		filterButton: PropTypes.string,
	}).isRequired,
	displayFilterMessage: PropTypes.bool.isRequired,
	filterableOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
	filters: PropTypes.instanceOf(ProductFilters),
	filtersOn: PropTypes.bool.isRequired,
	getSpecAttributes: PropTypes.func.isRequired,
	receiveFilters: PropTypes.func.isRequired,
	resetFilters: PropTypes.func.isRequired,
	updateFetchError: PropTypes.func.isRequired,
	theme: PropTypes.instanceOf(Object).isRequired,
};

FilterContainer.defaultProps = {
	catIdTerm: null,
	filters: {},
};

const mapStateToProps = state => {
	const attributes = state.productFilter.get('attributes');
	const attributeOptions = state.productFilter.get('attributeOptions');
	const filterableOptions = state.productFilter.get('filterableOptions');
	const filters = state.productFilter.get('filters');
	return {
		attributes,
		attributeOptions,
		filterableOptions,
		filters,
	};
};

const mapDispatchToProps = dispatch => bindActionCreators(productFilterActions, dispatch);

// for testing purposes
export { FilterContainer as FilterContainerClass, mapStateToProps };

export default compose(
	connect(mapStateToProps, mapDispatchToProps),
	withStyles(stylesMUI, { name: 'FilterContainer' }),
	withTheme,
)(toJS(FilterContainer));
